import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DialogRef } from '../modal/dialog-ref';
import { DIALOG_DATA } from '../modal/dialog-tokens';
import { CoreLibModalComponent } from '../modal/modal.component';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { OnsiteGuests } from 'app/shared/cases/types/OnsiteGuests.model';
import { CasesService } from 'app/shared/cases';
import { Select, Store } from '@ngxs/store';
import { Team } from 'app/new-fs/gestor-usuarios/teams/model/teams.model';
import { DefaultLanguageState } from 'app/states/default-language/default-language.state';
import { BreakpointService } from 'app/core-lib/services/breakpoints/breakpoint.service';
import { UsersState } from 'app/states/users/users.state';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { User } from 'app/shared/model';
import { ConfigCasesService } from 'app/cases/services/config-cases.service';
import { CasesLimit, DetectedCase, SimpleRelatedArea } from './model/detected-case.model';
import { ScheduleData } from 'app/shared/model/schedule.model';
import { UtilService } from 'app/shared/util.service';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';

/** Códigos del backend que el usuario puede corregir sin salir del formulario. */
const CREATE_ERROR_MESSAGES: Record<number, string> = {
  8013: 'errors.cases.team_without_active_users',
};
/** Límite mensual de casos: no se corrige en el formulario, se deriva al modal de upselling. */
const MONTHLY_LIMIT_CODE = 4007;

@Component({
    selector: 'mh-core-lib-create-case-modal',
    templateUrl: './create-case-modal.component.html',
    styleUrls: ['./create-case-modal.component.scss'],
    standalone: false
})
export class CoreLibCreateCaseModalComponent extends CoreLibModalComponent implements OnInit, OnDestroy {
  @ViewChild('createCaseContainer') createCaseContainer: ElementRef<HTMLDivElement>;
  @Select(UsersState.currentUsers) currentUsers$: Observable<any>;
  @Select(DefaultLanguageState.languageId) languageId$: Observable<number>;

  subs: Subscription = new Subscription();
  todayDate = new Date()
    .toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .split('/')
    .reverse()
    .join('-');
  types = [];
  priorities = [];
  states = [];
  productItemType = [];
  areas = [];
  teams: Team[] = [];
  users: User[] = [];
  onsiteGuests: OnsiteGuests[] = [];
  caseForm: FormGroup;
  assignOptions: Team[] | User[] = [];
  assignTypes: string[] = ['person', 'team'];
  assignOpen = false;
  assignSelected = 'person';
  timeTypes: string[] = ['minutes', 'hours', 'days', 'date'];
  timeOpen = false;
  timeSelected;
  uploadingFiles = false;
  clearAssignedAutocompleteControl: string;
  clearTimeAutocompleteControl: string;
  private OPEN_STATE = { id: 1 };
  currentCustomerId = this.store.selectSnapshot((state) => state.customers.currentCustomer?.id);
  currentLanguage = this.store.selectSnapshot((state) => state.defaultLanguage.code);

  displayFnTypes;
  displayFnPriorities;
  displayChipsFn;
  mentionState = [];
  maxTitleLength = 500;
  maxDescriptionLength = 3000;
  flagConditionalExpired = false;
  loadingDetectedCase = false;
  detectedCaseData;
  casesLimit: CasesLimit;
  discardLoading = false;
  showUpSellingModal = false;
  features = ['feature_1', 'feature_2', 'feature_3', 'feature_4'];
  scheduleData: ScheduleData;
  discardCaseView = false;
  reasonForm: FormGroup;
  langId;
  reasons;
  showDetectedCases = false;
  selectedDetectedAreas: SimpleRelatedArea[] = [];
  /** Request de creación en vuelo: bloquea el botón para evitar el doble envío, que ahora es
   *  posible porque el modal se queda abierto ante un error. */
  saving = false;
  /** Clave i18n del error del último intento; vacía si no hubo. */
  submitErrorKey = '';

  autocompleteFilterFn = (options, value) => {
    return options.filter((option: any) => {
      return (
        option.first_name.toLowerCase().includes(value.toLowerCase()) ||
        `${option.first_name.toLowerCase()} ${option.last_name.toLowerCase()}`.includes(value.toLowerCase()) ||
        option.email.toLowerCase().includes(value.toLocaleLowerCase())
      );
    });
  };

  autocompleteTeamsFilterFn = (options, value) => {
    if (options && options.length > 0) {
      return options.filter((option: any) => {
        return option.name.toLowerCase().includes(value.toLowerCase() || value);
      });
    }
  };

  autocompleteOnsiteGuestsFilterFn = (options, value) => {
    if (options && options.length > 0) {
      return options.filter((option: any) => {
        return (
          option.guest_name.toLowerCase().includes(value.toLowerCase()) ||
          `${option.guest_name.toLowerCase()} ${option.guest_last_name.toLowerCase()}`.includes(value.toLowerCase()) ||
          option.guest_room.toLowerCase().includes(value.toLowerCase())
        );
      });
    }
  };

  displayUsersOptions = (option) => {
    if (!option) return '';
    return `${option.first_name} ${option.last_name} - ${option?.company_position}`;
  };

  displayTeamsOptions = (option) => {
    return option.name;
  };

  displayUserEmailOptions = (option) => {
    return option.email;
  };

  displayOnsiteGuestsOptions = (option) => {
    const checkout = this.datePipe.transform(option.guest_checkout, 'dd MMMM yyyy');
    return `${option.guest_name} ${option.guest_last_name} - ${option.guest_room} - Check out ${checkout}`;
  };

  constructor(
    protected dialogRef: DialogRef,
    @Inject(DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private casesService: CasesService,
    private store: Store,
    private breakpointService: BreakpointService,
    private configCasesService: ConfigCasesService,
    private utilService: UtilService,
    private alertService: AlertService,
  ) {
    super(dialogRef, data);
    this.dialogRef.emitEvent(true);
  }

  ngOnInit(): void {
    this.handleRejectReasons();
    this.init();
  }

  handleRejectReasons() {
    this.reasons = this.data.reasonsDiscarded;
    this.reasonForm = this.fb.group({
      reason: [null, [Validators.required]],
      reasonComment: '',
    });
    this.subs.add(
      this.languageId$.subscribe((langId) => {
        this.langId = langId;
        this.reasons = this.reasons.map((reason) => {
          return {
            ...reason,
            reason: this.displayFn(reason),
          };
        });
      }),
    );
  }

  displayFn(option) {
    const translated = option.translates.find((translate) => translate.language_id === this.langId)?.name ?? null;
    return translated ? translated.charAt(0).toUpperCase() + translated.slice(1).toLowerCase() : null;
  }

  init(getData = true): void {
    this.flagConditionalExpired = this.configCasesService.getFlagExpired();
    this.timeSelected = 'hours';

    const casesLimit$ = this.configCasesService.getCasesLimit(this.currentCustomerId);
    const scheduleData$ = this.casesService.getScheduleData(this.currentCustomerId);

    if (this.data.detectedCase && (this.data?.item?.answer?.detectedCase || this.data?.item?.detected_cases)) {
      this.loadingDetectedCase = true;

      const detectedCase$ = this.casesService.getDetectedCaseByType(
        this.data?.item?.answer?.detectedCase ? 'survey-answer' : 'review',
        this.data?.item?.answer?.detectedCase ? this.data.item.answer.id : this.data.item.id,
      );

      forkJoin({ detectedCase: detectedCase$, casesLimit: casesLimit$, scheduleData: scheduleData$ }).subscribe(
        ({ detectedCase, casesLimit, scheduleData }) => {
          this.scheduleData = scheduleData;
          this.casesLimit = casesLimit;
          this.detectedCaseData = detectedCase;
          this.setForm(detectedCase);
          this.setDetectedAreas(detectedCase);
          this.loadingDetectedCase = false;
        },
        (error) => {
          if (error.status === 'NOT_FOUND') {
            this.setBasicDetectedCaseForm();
          }
          console.error(error);
        },
      );
    } else {
      this.setBasicDetectedCaseForm();
    }

    if (getData) {
      this.getUsers();
      this.getTeams();
      this.setData();
    }
  }

  setBasicDetectedCaseForm() {
    const casesLimit = this.configCasesService.getCasesLimit(this.currentCustomerId);
    const scheduleData = this.casesService.getScheduleData(this.currentCustomerId);
    this.setForm();
    forkJoin({ casesLimit, scheduleData }).subscribe(({ casesLimit, scheduleData }) => {
      this.scheduleData = scheduleData;
      this.casesLimit = casesLimit;
      this.loadingDetectedCase = false;
    });
  }

  setForm(data: DetectedCase = null) {
    this.caseForm = this.fb.group({
      title: [data?.title ?? '', [Validators.required, Validators.maxLength(this.maxTitleLength)]],
      description: [data?.description ?? '', [Validators.required, Validators.maxLength(this.maxDescriptionLength)]],
      state: [this.OPEN_STATE, [Validators.required]],
      priority: [data?.priority ?? '', [Validators.required]],
      case_type: [data?.case_type ?? '', [Validators.required]],
      author: [this.currentUser],
      assigned: [null, [Validators.required]],
      simple_related_areas: [data?.simple_related_areas ?? []],
      guest_room: [data?.guest_room ?? null],
      time_type: this.fb.group({
        type_time: [data?.time_type.type_time ?? this.timeSelected.toUpperCase(), [Validators.required]],
        total: [
          data?.time_type?.date ? data?.time_type?.date : data?.time_type?.total,
          this.timeSelected === 'date' || this.flagConditionalExpired ? [Validators.required] : null,
        ],
        date: [null, this.timeSelected === 'date' ? [Validators.required] : null],
      }),
      attachments: [null],
      is_of_team: [false],
    });

    this.timeSelected = data?.time_type?.type_time?.toLowerCase() ?? 'hours';

    this.setItem();
  }

  setItem() {
    let productItem: any;
    if (this.isNps) {
      productItem = {
        item_id: this.item.answer.id,
        product_id: this.currentProductId, //1 followup / 2 onsite
        type: { id: 1 },
      };
    }
    if (this.isReview) {
      productItem = {
        item_id: this.item.id,
        product_id: 3,
        type: { id: 2 },
      };
    }
    if (this.isPrestayPrecheckin) {
      productItem = {
        item_id: this.item.checkin_id,
        product_id: 4,
        type: { id: 3 },
      };
    }
    if (this.isPrestayOrder) {
      productItem = {
        item_id: this.item.order.id,
        product_id: 4,
        type: { id: 4 },
      };
    }

    this.caseForm.addControl('product_item', new FormControl(productItem));
  }

  setData() {
    this.types = this.data.types;
    this.priorities = this.data.priorities;
    this.productItemType = this.data.productItemType;
    this.states = this.data.states;
    this.areas = this.data.areas;
    this.onsiteGuests = this.data.onsiteGuests;
    this.displayFnTypes = this.data.translatesPipe.types;
    this.displayFnPriorities = this.data.translatesPipe.priorities;
    this.displayChipsFn = this.displayTeamsOptions;
    this.showDetectedCases = this.data.detectedCase;
  }

  setDetectedAreas(detectedCase: DetectedCase) {
    this.selectedDetectedAreas = this.mapDetectedAreasById(detectedCase?.simple_related_areas ?? [], this.areas);
    this.caseForm.get('simple_related_areas').setValue(this.selectedDetectedAreas);
  }

  mapDetectedAreasById(
    detectedAreas: SimpleRelatedArea[] = [],
    localizedAreas: SimpleRelatedArea[] = [],
  ): SimpleRelatedArea[] {
    return detectedAreas.map((area) => {
      const match = localizedAreas.find((option) => option.id === area.id);
      return match ? { ...match } : { ...area };
    });
  }

  // Prevención, no cosmética: un caso creado sobre un usuario desactivado o un equipo eliminado
  // nace bloqueado en el detalle, y el equipo de un caso ya creado no se puede cambiar.
  getUsers() {
    // `flag_deleted` = desactivado de la plataforma
    this.users = (this.store.selectSnapshot(UsersState.usersForCases) || []).filter(
      (user) => !user.flag_deleted,
    );
    this.assignOptions = this.users;
  }

  async getTeams() {
    try {
      const resp = await this.casesService.getTeams(this.currentCustomerId).toPromise();
      // acá `deleted` es a nivel de equipo: el equipo fue eliminado
      this.teams = (resp || []).filter((team) => !team.deleted);
    } catch (error) {
      console.error(error);
    }
  }

  setMentionData() {
    const value = this.caseForm?.value?.description;
    const matches = value.match(/\@\[(.*?)\]/g) || [];
    matches.forEach((match) => {
      const mention = this.mentionState.find((option) => option.key === match);
      if (mention) {
        this.caseForm.value.description = this.caseForm?.value?.description.replace(match, mention.value);
      }
    });
  }

  handleFilesUploaded(files) {
    const savedFiles = this.caseForm.get('attachments').value || [];
    this.caseForm.get('attachments').setValue([...savedFiles, ...files]);
  }

  removeUploadedFile(fileToRemove: { file_key: string; file_name: string }) {
    const currentFiles = this.caseForm.get('attachments').value || [];
    const updatedFiles = currentFiles.filter(file => file.file_key !== fileToRemove.file_key);
    this.caseForm.get('attachments').setValue(updatedFiles);
  }

  handleMentionState(event) {
    this.mentionState = event;
  }

  handleAssignOption(option) {
    this.caseForm.get('assigned').setValue(option);
  }

  handleGuestOrRoomOption(event) {
    const guestId = event?.guest_id;
    this.caseForm.get('guest_room').setValue(guestId);
  }

  handleAreasInput(options) {
    const areas = [...options];
    this.caseForm.get('simple_related_areas').setValue(areas);
  }

  handleAssignType($event: string) {
    this.clearAssignedAutocompleteControl = $event;
    this.assignOpen = false;
    this.assignSelected = $event;
    this.assignOptions = $event === 'person' ? this.users : this.teams;
    this.caseForm.get('is_of_team').setValue($event === 'team');
  }

  handleEmptyAssignInput($event: string) {
    if (!$event) {
      this.caseForm.get('assigned').reset();
    }
  }

  handleTimeType($event: string) {
    this.clearTimeAutocompleteControl = $event;
    this.timeOpen = false;
    this.timeSelected = $event;
    this.caseForm.get('time_type').get('type_time').setValue($event.toUpperCase());
  }

  handleAssignSelectorClick() {
    if (!this.isMobile) {
      this.assignOpen = !this.assignOpen;
    }
  }

  get isMonthlyLimitReached() {
    return (
      this.casesLimit?.assisted + this.casesLimit?.manual + 1 > this.casesLimit?.monthly_limit &&
      this.casesLimit?.unlimited === false
    );
  }

  /**
   * Arma el payload SIN mutar el formulario. Antes el ajuste de `time_type` se hacía con
   * setValue() sobre el form, lo que era inocuo porque el modal siempre se cerraba. Ahora que
   * un error deja el modal abierto y permite reintentar, mutarlo perdería la fecha de
   * vencimiento en el segundo intento (`total` ya vendría en null).
   */
  private buildCaseData() {
    this.setMentionData();

    const timeType = { ...this.caseForm.get('time_type').value };
    if (this.timeSelected === 'date') {
      timeType.date = timeType.total;
      timeType.total = null;
    }

    return {
      ...this.caseForm.value,
      time_type: timeType,
      generation_type_id:
        (this.data?.item?.answer?.detectedCase || this.data?.item?.detected_cases) && this.showDetectedCases
          ? this.detectedCaseData.generation_type_id
          : 2,
    };
  }

  /**
   * El POST se hace acá y el modal se cierra SOLO si sale bien. Así un error del backend deja
   * el formulario intacto y el usuario solo corrige lo que corresponda, en vez de perder todo
   * lo escrito (antes el guardado ocurría en el servicio, después de cerrar el modal).
   */
  async submit() {
    this.utilService.ga('cases', 'desk-click-create');

    if (this.isMonthlyLimitReached) {
      this.showUpSellingModal = true;
      return;
    }

    this.saving = true;
    this.submitErrorKey = '';
    try {
      const response = await this.casesService.save(this.buildCaseData(), this.currentCustomerId).toPromise();
      this.dialogRef.close({ createdCase: response });
    } catch (error) {
      // El interceptor ya desenvuelve la respuesta, así que `error.code` es el código del
      // backend directamente (ver http_interceptor.service.ts).
      // El límite mensual no se corrige en el formulario: se cierra para que el servicio abra
      // el modal de upselling, igual que antes.
      if (error?.code === MONTHLY_LIMIT_CODE) {
        this.dialogRef.close({ limitError: true });
        return;
      }
      this.submitErrorKey = CREATE_ERROR_MESSAGES[error?.code] ?? 'errors.cases.create_default';
      this.utilService.ga('cases', 'desk-create-case-error', String(error?.code ?? 'unknown'));
    } finally {
      this.saving = false;
    }
  }

  close() {
    this.utilService.ga('cases', 'desk-click-cancel-case');
    this.dialogRef.close();
  }

  scrollToBottom() {
    this.createCaseContainer.nativeElement.scrollTop = this.createCaseContainer.nativeElement.scrollHeight;
  }

  discardCase() {
    this.utilService.ga('cases', 'desk-click-discard-proposal');
    this.discardLoading = true;
    const discardCaseObs = this.casesService.discardDetectedCaseByType(
      this.data?.item?.answer?.detectedCase ? 'survey-answer' : 'review',
      this.data?.item?.answer?.detectedCase ? this.data.item.answer.id : this.data.item.id,
      this.reasonForm.value.reason ?? null,
      this.reasonForm.value.reasonComment ?? null,
    );
    discardCaseObs.subscribe(
      (result) => {
        if (result) {
          this.resetModalState();
          this.casesService.caseDetectedDiscarded$.next(true);
          this.alertService.handleAlert(
            'user_management.actions.success',
            5000,
            'bottom',
            'end',
            'snackbar-panel-success-user-config',
          );
        }
      },
      (error) => {
        console.error(error);
        this.alertService.handleAlert('user_management.actions.error', 5000, 'bottom', 'end', 'snackbar-panel-error');
      },
      () => {
        this.discardCaseView = false;
      },
    );
  }

  get currentOption(): number {
    return this.reasonForm.get('reason').value;
  }

  resetModalState() {
    this.casesLimit = null;
    this.detectedCaseData = null;
    if (this.data?.item?.answer?.detectedCase) {
      this.data.item.answer.detectedCase = false;
    }
    if (this.data?.item?.detected_cases) {
      const newItem = { ...this.data.item, detected_cases: false };
      this.data.item = newItem;
    }
    this.caseForm = undefined;
    this.init(false);
    this.discardLoading = false;
  }

  onDecline() {
    this.showUpSellingModal = false;
    this.utilService.ga('cases', 'desk-click-rejectunlimited');
    this.close();
  }

  scheduleDemo() {
    this.utilService.ga('cases', 'desk-click-bookdemo');
    this.casesService.sendScheduleNotification(this.currentCustomerId).subscribe();
    window.open(this.scheduleData.calendar, '_blank');
    this.close();
  }

  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.unsubscribe();
    }
  }

  get currentUser() {
    return this.data.currentUser;
  }

  get isFromProduct() {
    return this.data?.associated;
  }

  get item() {
    return this.data?.item;
  }

  get isReview() {
    return this.data.associated === 'review';
  }

  get isNps() {
    return this.data.associated === 'nps';
  }

  get isPrestayOrder() {
    return this.data.associated === 'order';
  }

  get isPrestayPrecheckin() {
    return this.data.associated === 'precheckin';
  }

  get currentProductId() {
    return this.data.product;
  }

  get isMobile() {
    return this.breakpointService.isMobile();
  }
}
